import { api } from '@packages/backend/convex/_generated/api';
import {
  calcUsageCostCents,
  formatCents,
  getUserErrorMessage,
  type SessionCvx,
  type SiteCvx,
} from '@packages/shared';
import { useNavigate } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { Droplets, type LucideIcon, Zap } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { cn } from '@/utils/cn';

/**
 * Power / water status tile. These report state, they aren't tappable — solid
 * brand green stays reserved for the action button below, so an ON meter gets a
 * subtle tint of it instead. An OFF meter has to look switched off, so it drops
 * the green entirely for the muted surface rather than dimming it.
 */
const MeterTile = ({
  icon: Icon,
  label,
  isOn,
  isSwitching,
}: {
  icon: LucideIcon;
  label: string;
  isOn: boolean;
  isSwitching: boolean;
}) => (
  <div
    className={cn(
      'flex flex-col items-center gap-2 rounded-xl border p-4 transition-colors',
      isOn
        ? 'border-primary/40 bg-primary/20 text-accent-foreground'
        : 'border-border bg-muted text-muted-foreground',
    )}
  >
    <Icon className="size-6" />
    <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
    <div className="flex h-7 items-center">
      {isSwitching ? (
        <LoadingIcon className="size-6 text-current" />
      ) : (
        <span className="text-lg font-bold">{isOn ? 'ON' : 'OFF'}</span>
      )}
    </div>
  </div>
);

export const ActiveSessionContent = ({
  session,
  site,
  sessionUuid,
  depositAmountCents,
  onCheckingOut,
}: {
  session: SessionCvx;
  site: SiteCvx | null;
  sessionUuid: string;
  depositAmountCents: number;
  onCheckingOut: (value: boolean) => void;
}) => {
  const convex = useConvex();
  const navigate = useNavigate();
  const [isSwitching, setIsSwitching] = useState(false);
  const [isCheckingUsage, setIsCheckingUsage] = useState(false);

  const hasPaid = session.payments.length > 0;

  const handleCheckUsage = async () => {
    setIsCheckingUsage(true);
    try {
      await convex.action(api.sessions.checkUsage, { sessionUuid });
      toast.success('Usage updated.');
    } catch (error) {
      toast.error(getUserErrorMessage(error));
    } finally {
      setIsCheckingUsage(false);
    }
  };

  const handleSwitch = async (status: 'ON' | 'OFF') => {
    setIsSwitching(true);
    try {
      await convex.action(api.sessions.switchMeter, { sessionUuid, status });
      toast.success(status === 'ON' ? 'Power turned on.' : 'Power turned off.');
    } catch (error) {
      toast.error(getUserErrorMessage(error));
    } finally {
      setIsSwitching(false);
    }
  };

  const totalPayments = session.payments.reduce((sum, p) => sum + p.amount, 0);
  const electricityCostCents = calcUsageCostCents(
    session.electricityUnits,
    session.electricityUnitPrice,
  );
  const waterCostCents = calcUsageCostCents(session.waterUnits, session.waterUnitPrice);
  const balanceCents = totalPayments - electricityCostCents - waterCostCents;
  const usedCents = electricityCostCents + waterCostCents;
  const usedPct = totalPayments > 0 ? Math.min(100, (usedCents / totalPayments) * 100) : 0;
  const powerOn = site?.lastElectricityStatus === 'ON';
  const waterOn = site?.lastWaterStatus === 'ON';

  return (
    <>
      <div className="space-y-4 rounded-2xl border bg-card p-5">
        {!hasPaid && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold tracking-tight">Welcome, {session.firstName}</h2>
            <p className="text-sm text-muted-foreground">
              Complete your deposit to activate your site.
            </p>
          </div>
        )}

        <div>
          <p className="text-sm text-muted-foreground">Balance remaining</p>
          <div className="flex items-center justify-between gap-2">
            <p className="text-4xl font-bold tracking-tight text-foreground">
              {formatCents(balanceCents, '$')}
            </p>
            {hasPaid && (
              <ActionButton
                variant="outline"
                size="sm"
                className="rounded-full border-0 bg-secondary px-4 font-semibold text-secondary-foreground hover:bg-secondary/80"
                onClick={() => navigate({ to: '/s/$sessionUuid/payment', params: { sessionUuid } })}
              >
                Top up
              </ActionButton>
            )}
          </div>
        </div>

        {hasPaid && (
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total used</span>
              <span className="flex items-center gap-1">
                <span className="font-bold text-foreground">{formatCents(usedCents, '$')}</span>
                <span className="text-muted-foreground">/ {formatCents(totalPayments, '$')}</span>
              </span>
            </div>
            <div className="mt-2 h-6 w-full rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{
                  width: `${usedPct}%`,
                  minWidth: usedCents > 0 ? '1.5rem' : undefined,
                }}
              />
            </div>
            <div className="mt-1 flex justify-between text-xs text-muted-foreground">
              <span>$0</span>
              <span>${Math.round(totalPayments / 100)}</span>
            </div>

            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between rounded-xl bg-background p-3">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/20">
                    <Zap className="size-5 text-accent-foreground" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-foreground">Power</p>
                    <p className="text-xs text-muted-foreground">
                      {session.electricityUnits == null
                        ? '-'
                        : `${Math.round(session.electricityUnits * 100) / 100} kWh`}
                    </p>
                  </div>
                </div>
                <span className="font-bold text-foreground">
                  {formatCents(electricityCostCents, '$')}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-background p-3">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/20">
                    <Droplets className="size-5 text-accent-foreground" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-foreground">Water</p>
                    <p className="text-xs text-muted-foreground">
                      {session.waterUnits == null
                        ? '-'
                        : `${Math.round(session.waterUnits * 100) / 100} units`}
                    </p>
                  </div>
                </div>
                <span className="font-bold text-foreground">
                  {formatCents(waterCostCents, '$')}
                </span>
              </div>
            </div>
          </div>
        )}

        {!hasPaid && (
          <ActionButton
            className="w-full"
            size="lg"
            onClick={() =>
              navigate({
                to: '/s/$sessionUuid/payment',
                params: { sessionUuid },
                search: { type: 'deposit' },
              })
            }
          >
            Pay {formatCents(depositAmountCents, '$')} deposit
          </ActionButton>
        )}

        {hasPaid && (
          <ActionButton
            size="lg"
            className="w-full"
            loading={isCheckingUsage}
            disabled={!powerOn && !waterOn}
            onClick={handleCheckUsage}
          >
            Check usage
          </ActionButton>
        )}
      </div>

      <div className="space-y-4 rounded-2xl border bg-card p-5">
        <div className="grid grid-cols-2 gap-3">
          <MeterTile icon={Zap} label="Power" isOn={powerOn} isSwitching={isSwitching} />
          <MeterTile icon={Droplets} label="Water" isOn={waterOn} isSwitching={isSwitching} />
        </div>

        {site &&
          (powerOn && waterOn ? (
            <ActionButton
              size="lg"
              className="w-full"
              disabled={isSwitching}
              onClick={() => handleSwitch('OFF')}
            >
              Turn both OFF
            </ActionButton>
          ) : (
            <div className="space-y-2">
              <ActionButton
                size="lg"
                className="w-full"
                disabled={isSwitching || balanceCents <= 0}
                onClick={() => handleSwitch('ON')}
              >
                Turn both ON
              </ActionButton>
              {balanceCents <= 0 && (
                <p className="text-center text-sm text-muted-foreground">
                  Your balance is too low. Top up to turn your power and water back on.
                </p>
              )}
            </div>
          ))}
      </div>

      {site && (
        <ActionButton
          className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
          size="lg"
          variant="ghost"
          action={async () => {
            onCheckingOut(true);
            try {
              await convex.action(api.sessions.checkout, { sessionUuid });
              return { error: false };
            } catch (error) {
              return { error: true, message: getUserErrorMessage(error) };
            } finally {
              onCheckingOut(false);
            }
          }}
          requireAreYouSure
          areYouSureDescription="This will end your session and check you out of the site. Final meter readings will be recorded."
        >
          Checkout from site
        </ActionButton>
      )}
    </>
  );
};
